<template>
  <gas-page-shell :breadcrumb="['设备管理', '设备监控']">
    <div class="usage-row">
      <div v-for="u in usageCards" :key="u.label" class="usage-card" :style="{ borderColor: u.color }">
        <i :class="u.icon" :style="{ color: u.color }"></i>
        <div>
          <div class="u-label">{{ u.label }}</div>
          <div class="u-val" :style="{ color: u.color }">{{ u.value }} <small>Nm³</small></div>
        </div>
      </div>
    </div>

    <div class="search-section">
      <div class="section-title"><span class="title-bar"></span>检索条件</div>
      <el-form :inline="true" :model="query" size="small" label-width="96px" class="search-form search-form-layout">
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:160px">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备名称">
          <el-input v-model="query.deviceName" clearable placeholder="请输入设备名称" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="设备编号">
          <el-input v-model="query.deviceCode" clearable placeholder="设备唯一编号" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="query.terminalType" placeholder="请选择设备类型" style="width:160px" @change="onDeviceTypeChange">
            <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="showCustomProtocolFilter" label="协议">
          <el-select
            v-model="query.protocolId"
            filterable
            clearable
            placeholder="请选择协议"
            style="width:200px"
            @change="onCustomProtocolChange"
          >
            <el-option
              v-for="p in customProtocolOptions"
              :key="p.protocolId"
              :label="`${p.protocolName}${p.protocolCode ? '（' + p.protocolCode + '）' : ''}`"
              :value="p.protocolId"
            />
          </el-select>
        </el-form-item>
        <template v-if="advancedVisible">
          <el-form-item label="所属机构">
            <el-select v-model="query.orgId" clearable placeholder="全部机构" style="width:160px">
              <el-option v-for="o in orgOptions" :key="o.orgId" :label="o.orgName" :value="o.orgId" />
            </el-select>
          </el-form-item>
          <el-form-item label="型号">
            <el-select v-model="query.model" clearable filterable placeholder="全部型号" style="width:140px">
              <el-option v-for="m in modelOptions" :key="m" :label="m" :value="m" />
            </el-select>
          </el-form-item>
          <el-form-item label="启用状态">
            <el-select v-model="query.enabledStatus" clearable placeholder="全部状态" style="width:120px">
              <el-option label="已启用" value="0" />
              <el-option label="已停用" value="1" />
            </el-select>
          </el-form-item>
        </template>
        <div class="search-actions">
          <el-button type="text" class="btn-advanced" @click="advancedVisible = !advancedVisible">
            {{ advancedVisible ? '收起检索' : '高级检索' }}
            <i :class="advancedVisible ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        </div>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-toolbar">
        <div class="table-title-group">
          <span class="table-title">设备监控 <em>共 {{ total }} 台</em></span>
          <gas-table-toolbar
            v-if="viewMode === 'list'"
            :columns="tableColumnDefs"
            :visible-map.sync="tableColumnVisible"
            :selected-count="selectedIds.length"
            :show-batch-delete="false"
          />
        </div>
        <div class="toolbar-actions">
          <el-radio-group v-model="viewMode" size="small" @change="onViewModeChange">
            <el-radio-button label="card">卡片样式</el-radio-button>
            <el-radio-button label="list">列表样式</el-radio-button>
          </el-radio-group>
          <el-button size="small" icon="el-icon-refresh" circle @click="manualRefresh" />
        </div>
      </div>

      <div v-if="viewMode === 'list'" v-loading="loading">
        <el-empty
          v-if="showCustomProtocolFilter && !query.protocolId"
          description="请先选择协议，再显示该协议下的设备列表"
        />
        <el-table
          v-else
          :data="listRows"
          border
          size="small"
          :row-key="listRowKey"
          :span-method="monitorSpanMethod"
          :row-class-name="rowClass"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="44" :selectable="listRowSelectable" />
          <el-table-column v-if="colVisible('rowNo')" column-key="rowNo" label="序号" width="56" align="center">
            <template slot-scope="{ row }">{{ listRowNo(row) }}</template>
          </el-table-column>
          <el-table-column v-if="colVisible('siteName')" column-key="siteName" prop="siteName" label="站点名称" min-width="120" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.siteName || '—' }}</template>
          </el-table-column>
          <el-table-column v-if="colVisible('deviceInfo')" column-key="deviceInfo" label="设备信息" min-width="150">
            <template slot-scope="{ row }">
              <div class="name">{{ row.terminalName }}</div>
              <div class="code">{{ row.terminalCode }}</div>
            </template>
          </el-table-column>
          <el-table-column v-if="colVisible('subDevice')" column-key="subDevice" label="子设备" min-width="110" align="center">
            <template slot-scope="{ row }">
              <el-input
                v-if="isEditingSubLabel(row)"
                ref="subLabelInput"
                v-model="subLabelDraft"
                size="mini"
                maxlength="20"
                @click.native.stop
                @keyup.enter.native="saveSubLabel"
                @keyup.esc.native="cancelEditSubLabel"
                @blur="saveSubLabel"
              />
              <a
                v-else
                href="javascript:;"
                class="sub-label-edit"
                title="点击修改子设备名称"
                @click.stop="startEditSubLabel(row)"
              >{{ row._subLabel || '设备1' }}</a>
            </template>
          </el-table-column>
          <el-table-column v-if="colVisible('deviceType')" prop="deviceType" label="设备类型" width="96" />
          <template v-for="m in activeDeviceMetrics">
            <el-table-column
              v-if="colVisible(m.key)"
              :key="m.key"
              :label="metricColumnLabel(m)"
              :min-width="m.width || 120"
              align="right"
            >
              <template slot-scope="{ row }">
                <span class="list-metric">
                  <a
                    class="metric-link"
                    :class="metricClass(row, metricAlarmKey(m), metricValue(row, m))"
                    href="javascript:;"
                    @click.prevent="openTrend(row, metricTrendKey(m))"
                  >{{ metricDisplay(row, m) }}</a>
                </span>
              </template>
            </el-table-column>
          </template>
          <el-table-column v-if="colVisible('updateTime')" label="更新时间" min-width="170">
            <template slot-scope="{ row }">
              <span :class="{ 'time-offline': isDeviceOffline(row) }">
                <template v-if="isDeviceOffline(row)">最后在线 {{ cardUpdateTime(row) }}</template>
                <template v-else>{{ cardUpdateTime(row) }}</template>
              </span>
            </template>
          </el-table-column>
          <el-table-column v-if="colVisible('sampleTime')" column-key="sampleTime" label="采集时间" min-width="170">
            <template slot-scope="{ row }">
              <span :class="{ 'time-offline': isDeviceOffline(row) }">{{ sampleTimeText(row) }}</span>
            </template>
          </el-table-column>
          <el-table-column v-if="colVisible('status')" label="状态" width="80" align="center">
            <template slot-scope="{ row }"><gas-status-tag kind="online" :value="row.onlineStatus" /></template>
          </el-table-column>
          <el-table-column label="操作" width="150" fixed="right" align="center">
            <template slot-scope="{ row }">
              <el-button type="text" size="small" @click="viewDetail(row)">详情</el-button>
              <el-button type="text" size="small" @click="openAlarmSettings(row)">报警设置</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div v-else v-loading="loading" class="card-grid">
        <el-empty
          v-if="showCustomProtocolFilter && !query.protocolId"
          description="请先选择协议，再显示该协议下的设备列表"
        />
        <template v-else>
        <div v-for="row in cardList" :key="row.terminalId || row.terminalCode" class="mon-card" :class="{ alarm: row.alarm }">
          <div class="card-head">
            <div>
              <div class="card-name">{{ row.terminalName }}</div>
              <div class="card-code">编码 {{ row.terminalCode }}</div>
            </div>
            <div class="card-tools">
              <el-button
                type="text"
                icon="el-icon-setting"
                class="card-alarm-btn"
                title="报警设置"
                @click.stop="openAlarmSettings(row)"
              />
              <span class="dot" :class="row.onlineStatus"></span>
            </div>
          </div>
          <div v-if="row.alarmMessage" class="alarm-banner">{{ row.alarmMessage }}</div>
          <div class="card-time" :class="{ offline: isDeviceOffline(row) }">
            <template v-if="isDeviceOffline(row)">最后在线更新时间 {{ cardUpdateTime(row) }}</template>
            <template v-else>更新时间 {{ cardUpdateTime(row) }}</template>
          </div>
          <div v-if="subDeviceTabs(row).length > 1" class="sub-device-tabs">
            <button
              v-for="sub in subDeviceTabs(row)"
              :key="sub.code"
              type="button"
              class="sub-tab"
              :class="{ active: selectedSubDevice(row) === sub.code, editing: isEditingSubLabel(row, sub) }"
              title="再次点击可修改名称"
              @click.stop="onCardSubTabClick(row, sub)"
              @dblclick.stop="startEditSubLabel(row, sub)"
            >
              <input
                v-if="isEditingSubLabel(row, sub)"
                ref="subLabelInput"
                v-model="subLabelDraft"
                class="sub-tab-input"
                maxlength="20"
                @click.stop
                @keydown.enter.prevent="saveSubLabel"
                @keydown.esc.prevent="cancelEditSubLabel"
                @blur="saveSubLabel"
              >
              <span v-else>{{ sub.label }}</span>
            </button>
          </div>
          <div class="metrics" :class="metricGridClass(row)">
            <div v-for="m in metricsForRow(row)" :key="m.key" class="metric-cell" @click="openTrend(row, metricTrendKey(m))">
              <span class="metric-label">
                {{ m.label }}
              </span>
              <strong :class="metricClass(row, metricAlarmKey(m), metricValue(row, m))">
                {{ metricDisplay(row, m) }}<template v-if="m.unit && metricValue(row, m) != null && metricValue(row, m) !== ''"> {{ m.unit }}</template>
              </strong>
            </div>
          </div>
        </div>
        </template>
      </div>

      <el-pagination class="pager" background layout="total, sizes, prev, pager, next, jumper"
        :total="total" :current-page="query.pageNum" :page-size="query.pageSize"
        :page-sizes="[6, 10, 20, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <device-trend-dialog
      :visible.sync="trendDialogVisible"
      :device-id="trendDeviceId"
      :device-code="trendDeviceCode"
      :terminal-code="trendTerminalCode"
      :terminal-name="trendTerminalName"
      :sub-device-code="trendSubDeviceCode"
      :metric="trendMetric"
    />
    <device-detail-drawer
      :visible.sync="detailDrawerVisible"
      :device-id="detailDeviceId"
    />
    <device-alarm-rules-drawer
      :visible.sync="alarmDrawerVisible"
      :rules="alarmRules"
      :device="alarmDevice"
      :metrics="alarmMetrics"
      @saved="onAlarmRulesSaved"
    />
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import DeviceTrendDialog from '@/components/DeviceTrendDialog.vue'
import DeviceDetailDrawer from '@/components/DeviceDetailDrawer.vue'
import DeviceAlarmRulesDrawer from '@/components/DeviceAlarmRulesDrawer.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import { DEFAULT_MONITOR_DEVICE_TYPE, normalizeDeviceType, formatMonitorMetricValue } from '@/config/deviceTypeMetrics'
import { protocolParamsToMetrics, protocolsForDeviceType } from '@/config/protocolParamNames'
import { metricAlarmClass } from '@/utils/monitorAlarmThreshold'
import { gasApi } from '@/api/gas'

const MONITOR_BASE_COLUMNS = [
  { key: 'rowNo', label: '序号' },
  { key: 'siteName', label: '站点名称' },
  { key: 'deviceInfo', label: '设备信息' },
  { key: 'subDevice', label: '子设备' },
  { key: 'deviceType', label: '设备类型' }
]

const MONITOR_TAIL_COLUMNS = [
  { key: 'updateTime', label: '更新时间' },
  { key: 'sampleTime', label: '采集时间' },
  { key: 'status', label: '状态' }
]

function formatMetricColumnLabel(metric) {
  if (!metric) return ''
  return metric.unit ? `${metric.label} ${metric.unit}` : metric.label
}

function monitorColumnsForType() {
  return MONITOR_BASE_COLUMNS.concat(MONITOR_TAIL_COLUMNS)
}

function siteSortKey(row) {
  return ((row && row.siteName) || '').trim()
}

function compareZh(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'zh-CN')
}

function parseMonitorTimeMs(raw) {
  if (raw == null || raw === '') return 0
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  const text = String(raw).trim().replace('T', ' ').replace(/-/g, '/')
  const ms = new Date(text).getTime()
  return Number.isNaN(ms) ? 0 : ms
}

function latestMonitorTimeRaw(row) {
  if (!row) return ''
  const candidates = [row.lastCommTime, row.updateTime, row.lastOnlineTime, row.meterTime]
  if (Array.isArray(row.subDevices)) {
    row.subDevices.forEach(sub => {
      if (sub && sub.sampleTime) candidates.push(sub.sampleTime)
    })
  }
  let bestMs = 0
  let bestRaw = ''
  candidates.forEach(value => {
    const ms = parseMonitorTimeMs(value)
    if (ms > bestMs) {
      bestMs = ms
      bestRaw = value
    }
  })
  return bestRaw
}

function subDeviceSampleTimeRaw(device, sub, code) {
  if (sub && sub.sampleTime) return sub.sampleTime
  const list = (device && device.subDevices) || []
  for (let i = 0; i < list.length; i++) {
    const item = list[i]
    if (!item || !item.sampleTime) continue
    if (!code || !item.code || item.code === code) return item.sampleTime
  }
  return ''
}

function formatMonitorTime(raw) {
  const ms = parseMonitorTimeMs(raw)
  if (!ms) return '—'
  const d = new Date(ms)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function monitorCreateTimeMs(row) {
  if (!row) return 0
  return parseMonitorTimeMs(row.createTime)
}

function sortMonitorDevices(list) {
  return (list || []).slice().sort((a, b) => {
    const sa = siteSortKey(a)
    const sb = siteSortKey(b)
    if (!sa && sb) return 1
    if (sa && !sb) return -1
    const site = compareZh(sa, sb)
    if (site) return site
    const name = compareZh(a.terminalName || a.deviceName, b.terminalName || b.deviceName)
    if (name) return name
    return compareZh(a.terminalCode || a.deviceCode, b.terminalCode || b.deviceCode)
  })
}

function subDevicesForList(device) {
  const list = (device && device.subDevices) || []
  return list.length > 1 ? list : []
}

function defaultSubLabelFromCode(code) {
  const n = String(code || '').replace(/^m/i, '')
  return n ? `设备${n}` : '设备1'
}

function pickSubDeviceName(device, code) {
  const names = (device && device.subDeviceNames) || {}
  const custom = names[code] || names[String(code || '').toLowerCase()]
  return custom == null ? '' : String(custom).trim()
}

function listRowSubMeta(device, sub, expanded, subIndex) {
  if (sub && expanded) {
    const code = sub.code || ('m' + (subIndex + 1))
    const label = (sub.label && String(sub.label).trim()) || pickSubDeviceName(device, code) || defaultSubLabelFromCode(code)
    return { code, label }
  }
  const first = ((device && device.subDevices) || [])[0]
  const code = (first && first.code) || 'm1'
  const label = (first && first.label && String(first.label).trim())
    || pickSubDeviceName(device, code)
    || pickSubDeviceName(device, 'm1')
    || '设备1'
  return { code, label }
}

function spanCell(span) {
  if (span === 0) return { rowspan: 0, colspan: 0 }
  return { rowspan: span || 1, colspan: 1 }
}

function applyMonitorListSpans(rows) {
  let i = 0
  while (i < rows.length) {
    const site = rows[i]._siteKey
    let j = i + 1
    if (site) {
      while (j < rows.length && rows[j]._siteKey === site) j++
    }
    const span = j - i
    for (let k = i; k < j; k++) rows[k]._siteSpan = k === i ? span : 0
    i = j
  }
  i = 0
  while (i < rows.length) {
    const key = rows[i]._deviceKey
    let j = i + 1
    while (j < rows.length && rows[j]._deviceKey === key) j++
    const span = j - i
    for (let k = i; k < j; k++) rows[k]._deviceSpan = k === i ? span : 0
    i = j
  }
}

function buildMonitorListRows(list) {
  const devices = sortMonitorDevices(list)
  const rows = []
  devices.forEach((device, deviceIndex) => {
    const subs = subDevicesForList(device)
    const parts = subs.length ? subs : [null]
    parts.forEach((sub, subIndex) => {
      const metrics = (sub && sub.metrics) || {}
      const siteName = siteSortKey(device)
      const subMeta = listRowSubMeta(device, sub, !!subs.length, subIndex)
      rows.push(Object.assign({}, device, metrics, {
        _listRowKey: [device.terminalId || device.deviceId || device.terminalCode || deviceIndex, subMeta.code || 'main'].join('-'),
        _parentIndex: deviceIndex,
        _subIndex: subIndex,
        _subCount: parts.length,
        _subLabel: subMeta.label,
        _subCode: subMeta.code,
        _siteKey: siteName,
        _deviceKey: String(device.deviceId || device.terminalId || device.terminalCode || ('d' + deviceIndex)),
        updateTime: latestMonitorTimeRaw(Object.assign({}, device, { updateTime: (sub && sub.sampleTime) || device.updateTime })) || device.updateTime,
        sampleTime: subDeviceSampleTimeRaw(device, sub, subMeta.code) || device.sampleTime || device.meterTime || '',
        lastCommTime: latestMonitorTimeRaw(device) || device.lastCommTime,
        lastOnlineTime: latestMonitorTimeRaw(device) || device.lastOnlineTime
      }))
    })
  })
  applyMonitorListSpans(rows)
  return rows
}

export default {
  components: { GasPageShell, DeviceTrendDialog, DeviceDetailDrawer, DeviceAlarmRulesDrawer },
  mixins: [gasCrudPage, deviceTypeOptionsMixin, siteOptionsMixin],
  data() {
    return {
      autoLoad: false,
      loadOrgOptions: true,
      idField: 'terminalId',
      listApi: gasApi.terminal.monitor,
      saveApi: null,
      removeApi: null,
      viewMode: 'card',
      usageCards: [],
      modelOptions: [],
      alarmRules: [],
      advancedVisible: false,
      trendDialogVisible: false,
      trendDeviceId: null,
      trendDeviceCode: '',
      trendTerminalCode: '',
      trendTerminalName: '',
      trendSubDeviceCode: '',
      trendMetric: 'stdInstant',
      detailDrawerVisible: false,
      detailDeviceId: null,
      alarmDrawerVisible: false,
      alarmDevice: null,
      alarmMetrics: [],
      cardSubDevice: {},
      subLabelEditing: null,
      subLabelDraft: '',
      subLabelSaving: false,
      monitorMetricColumnSignature: '',
      customProtocolOptions: [],
      customProtocolMetrics: [],
      refreshTimer: null,
      refreshIntervalMs: 5000,
      refreshing: false,
      query: {
        pageNum: 1,
        pageSize: 6,
        deviceName: '',
        terminalType: DEFAULT_MONITOR_DEVICE_TYPE,
        deviceCode: '',
        siteName: '',
        orgId: null,
        model: '',
        enabledStatus: '',
        protocolId: null,
        protocolName: ''
      }
    }
  },
  computed: {
    isLowPowerDevice() {
      return normalizeDeviceType(this.query.terminalType) === '低功耗远传'
    },
    showCustomProtocolFilter() {
      return !this.isLowPowerDevice
    },
    activeDeviceMetrics() {
      return this.customProtocolMetrics || []
    },
    listRows() {
      if (normalizeDeviceType(this.query.terminalType) !== '压力远传') {
        return buildMonitorListRows(this.list)
      }
      const list = (this.list || []).map(row => Object.assign({}, row, { subDevices: [] }))
      return buildMonitorListRows(list)
    },
    cardList() {
      return (this.list || []).slice().sort((a, b) => {
        const tb = monitorCreateTimeMs(b)
        const ta = monitorCreateTimeMs(a)
        if (tb !== ta) return tb - ta
        return Number(b.terminalId || b.deviceId || 0) - Number(a.terminalId || a.deviceId || 0)
      })
    }
  },
  watch: {
    'query.terminalType'(val) {
      this.resetMonitorColumns(val)
    }
  },
  async created() {
    this.resetMonitorColumns(this.query.terminalType)
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadModelOptions()
    this.loadAlarmRules()
    this.loadUsage()
    await this.loadCustomProtocolOptions()
    await this.selectDefaultProtocol()
    this.loadData()
  },
  mounted() {
    this.startAutoRefresh()
  },
  activated() {
    this.startAutoRefresh()
  },
  deactivated() {
    this.stopAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    resetMonitorColumns(type) {
      const normalized = normalizeDeviceType(type)
      this.monitorMetricColumnSignature = ''
      this.initTableColumns(monitorColumnsForType(), `terminal-monitor-${normalized}-v5`)
    },
    syncMonitorMetricColumns() {
      const metrics = this.activeDeviceMetrics || []
      const signature = (this.viewMode || '') + '|' + normalizeDeviceType(this.query.terminalType) + '|' + metrics.map(m => m && m.key).filter(Boolean).join(',')
      if (signature === this.monitorMetricColumnSignature) return
      this.monitorMetricColumnSignature = signature
      const normalized = normalizeDeviceType(this.query.terminalType)
      const metricCols = metrics.map(m => ({ key: m.key, label: formatMetricColumnLabel(m) }))
      this.initTableColumns(MONITOR_BASE_COLUMNS.concat(metricCols).concat(MONITOR_TAIL_COLUMNS), `terminal-monitor-${normalized}-v5`)
    },
    onViewModeChange() {
      if (!this.query.terminalType) {
        this.query.terminalType = DEFAULT_MONITOR_DEVICE_TYPE
      }
      this.query.pageNum = 1
      this.$nextTick(() => this.loadData())
    },
    async onDeviceTypeChange(val) {
      this.query.protocolId = null
      this.query.protocolName = ''
      this.customProtocolMetrics = []
      this.list = []
      this.total = 0
      if (!val) this.query.terminalType = DEFAULT_MONITOR_DEVICE_TYPE
      this.query.pageNum = 1
      this.syncMonitorMetricColumns()
      await this.loadCustomProtocolOptions()
      if (this.isLowPowerDevice) {
        await this.selectLowPowerProtocol()
      }
    },
    async selectLowPowerProtocol() {
      const options = this.customProtocolOptions || []
      const hit = options.find(p => String((p && p.protocolCode) || '').toLowerCase() === 'digonghao10')
        || options.find(p => /低功耗远传全/.test(String((p && p.protocolName) || '')))
        || options.find(p => String((p && p.protocolType) || '').trim() === '低功耗远传')
        || null
      this.query.protocolId = hit ? hit.protocolId : null
      this.query.protocolName = hit ? (hit.protocolName || '') : ''
      await this.loadCustomProtocolColumns()
      this.loadData()
    },
    async loadCustomProtocolOptions() {
      try {
        const res = await gasApi.protocol.list({ pageNum: 1, pageSize: 500 })
        this.customProtocolOptions = protocolsForDeviceType((res.data && res.data.records) || [], this.query.terminalType)
      } catch (e) {
        this.customProtocolOptions = []
      }
    },
    async onCustomProtocolChange(protocolId) {
      const hit = (this.customProtocolOptions || []).find(p => p.protocolId === protocolId)
      this.query.protocolId = protocolId || null
      this.query.protocolName = hit ? (hit.protocolName || '') : ''
      this.query.pageNum = 1
      await this.loadCustomProtocolColumns()
      this.loadData()
    },
    defaultProtocolOption() {
      const options = this.customProtocolOptions || []
      const preset = options.find(p => /一拖二|1V2|1拖2/i.test((p && p.protocolName) || ''))
      return preset || options[0] || null
    },
    async selectDefaultProtocol() {
      const hit = this.defaultProtocolOption()
      this.query.protocolId = hit ? hit.protocolId : null
      this.query.protocolName = hit ? (hit.protocolName || '') : ''
      await this.loadCustomProtocolColumns()
    },
    async loadCustomProtocolColumns() {
      if (!this.query.protocolId) {
        this.customProtocolMetrics = []
        this.syncMonitorMetricColumns()
        return
      }
      try {
        const res = await gasApi.protocol.paramList({
          protocolId: this.query.protocolId,
          pageNum: 1,
          pageSize: 200
        })
        const records = (res.data && res.data.records) || []
        const protocol = (this.customProtocolOptions || []).find(p => p.protocolId === this.query.protocolId)
        this.customProtocolMetrics = protocolParamsToMetrics(records, this.query.terminalType, protocol)
      } catch (e) {
        this.customProtocolMetrics = []
      }
      this.syncMonitorMetricColumns()
    },
    metricsForRow() {
      return this.customProtocolMetrics || []
    },
    metricGridClass(row) {
      return this.metricsForRow(row).length <= 3 ? 'grid-3' : 'grid-2'
    },
    metricColumnLabel(metric) {
      return formatMetricColumnLabel(metric)
    },
    subDeviceTabs(row) {
      if (normalizeDeviceType(this.query.terminalType) === '压力远传') return []
      const list = (row && row.subDevices) || []
      return list.length > 1 ? list : []
    },
    selectedSubDevice(row) {
      if (!row) return 'm1'
      const key = row.terminalCode || row.deviceCode
      const saved = key ? this.cardSubDevice[key] : ''
      if (saved) return saved
      const tabs = this.subDeviceTabs(row)
      return (tabs[0] && tabs[0].code) || 'm1'
    },
    selectSubDevice(row, code) {
      const key = row && (row.terminalCode || row.deviceCode)
      if (!key) return
      this.$set(this.cardSubDevice, key, code)
    },
    onCardSubTabClick(row, sub) {
      if (!sub) return
      if (this.selectedSubDevice(row) === sub.code && !this.isEditingSubLabel(row, sub)) {
        this.startEditSubLabel(row, sub)
        return
      }
      this.selectSubDevice(row, sub.code)
    },
    defaultSubLabel(code) {
      return defaultSubLabelFromCode(code)
    },
    isEditingSubLabel(row, sub) {
      if (!this.subLabelEditing || !row) return false
      const code = (sub && sub.code) || row._subCode || 'm1'
      return this.subLabelEditing.deviceId === row.deviceId && this.subLabelEditing.code === code
    },
    startEditSubLabel(row, sub) {
      const code = (sub && sub.code) || row._subCode || 'm1'
      const label = (sub && sub.label) || row._subLabel || this.defaultSubLabel(code)
      if (!row || !row.deviceId || !code) {
        this.$message.warning('当前记录未绑定设备，无法保存子设备名称')
        return
      }
      this.subLabelEditing = { deviceId: row.deviceId, code }
      this.subLabelDraft = label
      this.focusSubLabelInput()
    },
    focusSubLabelInput() {
      this.$nextTick(() => {
        const ref = this.$refs.subLabelInput
        const el = Array.isArray(ref) ? ref[0] : ref
        if (!el) return
        if (typeof el.focus === 'function' && el.$el) {
          el.focus()
          const native = el.$el.querySelector('input')
          if (native && native.select) native.select()
          return
        }
        if (typeof el.focus === 'function') {
          el.focus()
          if (el.select) el.select()
        }
      })
    },
    cancelEditSubLabel() {
      this.subLabelEditing = null
      this.subLabelDraft = ''
    },
    applyLocalSubLabel(deviceId, code, label) {
      const hit = (this.list || []).find(r => r.deviceId === deviceId)
      if (!hit) return
      if (!hit.subDeviceNames) this.$set(hit, 'subDeviceNames', {})
      this.$set(hit.subDeviceNames, code, label)
      const subs = hit.subDevices || []
      const sub = subs.find(s => s.code === code)
      if (sub) this.$set(sub, 'label', label)
    },
    async saveSubLabel() {
      if (!this.subLabelEditing || this.subLabelSaving) return
      const { deviceId, code } = this.subLabelEditing
      const label = (this.subLabelDraft || '').trim() || this.defaultSubLabel(code)
      this.subLabelSaving = true
      try {
        await gasApi.device.saveSubDeviceName({ deviceId, subDeviceCode: code, label })
        this.applyLocalSubLabel(deviceId, code, label)
        this.cancelEditSubLabel()
      } catch (e) {
        this.$message.error((e && e.message) || '子设备名称保存失败')
      } finally {
        this.subLabelSaving = false
      }
    },
    metricSourceRow(row) {
      const tabs = this.subDeviceTabs(row)
      if (this.viewMode === 'list' || !tabs.length) return row
      const code = this.selectedSubDevice(row)
      const hit = tabs.find(s => s.code === code) || tabs[0]
      return Object.assign({}, row, (hit && hit.metrics) || {})
    },
    metricValue(row, metric) {
      const source = this.metricSourceRow(row)
      if (!source || !metric) return null
      if (metric.key === 'outletPressure') {
        return source.outletPressure != null ? source.outletPressure : source.pressure
      }
      if (metric.key === 'leakage') {
        if (source.leakage != null) return source.leakage
        if (source.inletLeak != null) return source.inletLeak
        return source.concentration
      }
      if (source[metric.key] != null && source[metric.key] !== '') return source[metric.key]
      if (metric.label && source[metric.label] != null && source[metric.label] !== '') return source[metric.label]
      return null
    },
    metricDisplay(row, metric) {
      const value = this.metricValue(row, metric)
      const text = formatMonitorMetricValue(value, metric)
      if (metric && metric.key === 'leakage' && text !== '—' && row.concentrationStatus) {
        return `${text} ${row.concentrationStatus}`
      }
      return text
    },
    metricTrendKey(metric) {
      return metric && metric.key ? metric.key : 'stdInstant'
    },
    metricAlarmKey(metric) {
      return metric && metric.key ? metric.key : ''
    },
    async loadAlarmRules() {
      try {
        const res = await gasApi.alarmRule.list({ pageNum: 1, pageSize: 500 })
        this.alarmRules = (res.data && res.data.records) || []
      } catch (e) {
        this.alarmRules = []
      }
    },
    metricClass(row, metricKey, value) {
      const deviceType = row.deviceType || row.terminalType || ''
      const deviceCode = row.deviceCode || row.terminalCode || ''
      const siteName = row.siteName || ''
      return metricAlarmClass(value, this.alarmRules, deviceType, metricKey, deviceCode, siteName)
    },
    outletValue(row) {
      const v = row.outletPressure != null ? row.outletPressure : row.pressure
      return v
    },
    displayVal(v) {
      return v == null || v === '' ? '—' : v
    },
    openAlarmSettings(row) {
      this.alarmDevice = row || null
      this.alarmMetrics = this.metricsForRow(row)
      this.alarmDrawerVisible = true
    },
    async onAlarmRulesSaved() {
      await this.loadAlarmRules()
    },
    async loadUsage() {
      const res = await gasApi.terminal.monitorUsage()
      const d = res.data || {}
      this.usageCards = [
        { label: '上月用量', value: (d.lastMonthUsage || 0).toLocaleString(), icon: 'el-icon-date', color: '#1677ff' },
        { label: '当月用量', value: (d.currentMonthUsage || 0).toLocaleString(), icon: 'el-icon-data-line', color: '#00b42a' },
        { label: '累计用量', value: (d.totalUsage || 0).toLocaleString(), icon: 'el-icon-pie-chart', color: '#ff7d00' }
      ]
    },
    async loadModelOptions() {
      try {
        const res = await gasApi.device.modelOptions()
        this.modelOptions = res.data || []
      } catch (e) {
        this.modelOptions = []
      }
    },
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => {
        if (p[k] === '' || p[k] === null) delete p[k]
      })
      return p
    },
    async loadData(options = {}) {
      if (this.showCustomProtocolFilter && !this.query.protocolId) {
        this.list = []
        this.total = 0
        this.syncMonitorMetricColumns()
        return
      }
      const silent = options && options.silent === true
      if (!silent) this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
        this.syncMonitorMetricColumns()
      } finally {
        if (!silent) this.loading = false
      }
    },
    manualRefresh() {
      this.loadUsage()
      this.loadData()
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.refreshTimer = window.setInterval(this.refreshRealtimeData, this.refreshIntervalMs)
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    async refreshRealtimeData() {
      if (this.refreshing || this.loading || this.subLabelEditing) return
      this.refreshing = true
      try {
        await this.loadData({ silent: true })
      } catch (e) {
        // 自动刷新失败时保持当前页面数据，下一轮继续刷新。
      } finally {
        this.refreshing = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    async resetSearch() {
      this.advancedVisible = false
      this.query = {
        pageNum: 1,
        pageSize: this.query.pageSize,
        deviceName: '',
        terminalType: DEFAULT_MONITOR_DEVICE_TYPE,
        deviceCode: '',
        siteName: '',
        orgId: null,
        model: '',
        enabledStatus: '',
        protocolId: null,
        protocolName: ''
      }
      this.customProtocolMetrics = []
      await this.loadCustomProtocolOptions()
      await this.selectDefaultProtocol()
      this.loadData()
    },
    rowNo(i) {
      return (this.query.pageNum - 1) * this.query.pageSize + i + 1
    },
    listRowKey(row) {
      return (row && row._listRowKey) || String((row && (row.terminalId || row.terminalCode)) || '')
    },
    listRowNo(row) {
      const idx = row && row._parentIndex != null ? row._parentIndex : 0
      return (this.query.pageNum - 1) * this.query.pageSize + idx + 1
    },
    listRowSelectable(row) {
      return !row || !row._subIndex
    },
    monitorSpanMethod({ row, column }) {
      const key = column.columnKey || column.property
      if (key === 'siteName') return spanCell(row._siteSpan)
      if (key === 'deviceInfo' || key === 'rowNo') return spanCell(row._deviceSpan)
      return { rowspan: 1, colspan: 1 }
    },
    rowClass({ row }) {
      return row.alarm ? 'row-alarm' : ''
    },
    viewDetail(row) {
      if (!row || !row.deviceId) {
        this.$message.warning('当前记录未绑定设备，无法查看真实详情')
        return
      }
      this.detailDeviceId = row.deviceId
      this.detailDrawerVisible = true
    },
    formatOutletKpa(row) {
      const v = row.outletPressure != null ? row.outletPressure : row.pressure
      return v != null ? v : '—'
    },
    isDeviceOffline(row) {
      const s = (row && row.onlineStatus) || ''
      return s === 'offline' || s === 'alarm'
    },
    cardUpdateTime(row) {
      return formatMonitorTime(latestMonitorTimeRaw(row))
    },
    sampleTimeText(row) {
      if (!row) return '—'
      return formatMonitorTime(row.sampleTime)
    },
    openTrend(row, metric) {
      this.trendDeviceId = row.deviceId || null
      this.trendDeviceCode = row.deviceCode || row.terminalCode || ''
      this.trendTerminalCode = row.terminalCode || ''
      this.trendTerminalName = row.terminalName || ''
      this.trendSubDeviceCode = row._subCode || (this.subDeviceTabs(row).length > 1 ? this.selectedSubDevice(row) : '')
      this.trendMetric = metric || 'stdInstant'
      this.trendDialogVisible = true
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';

.usage-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.usage-card {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-left-width: 4px;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  i { font-size: 28px; }
}
.u-label { font-size: 13px; color: #86909c; }
.u-val { font-size: 22px; font-weight: 600; small { font-size: 12px; font-weight: 400; } }

.search-section {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 12px;
}
.title-bar {
  width: 3px;
  height: 14px;
  background: #1677ff;
  border-radius: 2px;
  margin-right: 8px;
}
.search-form-layout {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
}
.search-form-layout >>> .el-form-item {
  margin-bottom: 12px;
  margin-right: 0;
}
.search-actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  margin-bottom: 12px;
  white-space: nowrap;
}
.search-actions .el-button + .el-button {
  margin-left: 8px;
}
.btn-advanced {
  padding-left: 0;
  margin-right: 4px;
}

.table-toolbar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
.toolbar-actions { display: flex; align-items: center; gap: 8px; }
.name { font-weight: 500; color: #1d2129; }
.code { font-size: 12px; color: #86909c; }
>>> .el-table .el-table__body td {
  vertical-align: middle;
}
>>> .row-alarm { background: #fff1f0 !important; }
>>> .row-alarm .cell { color: #f53f3f; }

.card-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.mon-card {
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
}
.mon-card.alarm { border-color: #ffccc7; background: #fff7f6; }
.card-head { display: flex; justify-content: space-between; align-items: flex-start; }
.card-name { font-weight: 600; }
.card-code { font-size: 12px; color: #86909c; }
.card-tools {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
}
.card-alarm-btn {
  padding: 0;
  font-size: 18px;
  color: #86909c;
}
.card-alarm-btn:hover {
  color: #1677ff;
}
.dot { width: 10px; height: 10px; border-radius: 50%; }
.dot.online { background: #00b42a; }
.dot.offline, .dot.alarm { background: #f53f3f; }
.alarm-banner { background: #ffece8; color: #f53f3f; font-size: 12px; padding: 6px 8px; margin: 8px 0; border-radius: 4px; }
.card-time { font-size: 12px; color: #86909c; margin-bottom: 10px; }
.card-time.offline { color: #f53f3f; }
.sub-device-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.sub-tab {
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #4e5969;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 20px;
  cursor: pointer;
}
.sub-tab.active {
  border-color: #1677ff;
  background: #e6f4ff;
  color: #1677ff;
}
.sub-tab.editing {
  padding: 0 4px;
}
.sub-tab-input {
  width: 72px;
  border: none;
  outline: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: center;
}
.sub-label-edit {
  color: #1677ff;
  text-decoration: none;
}
.sub-label-edit:hover { text-decoration: underline; }
.metrics { display: grid; gap: 8px; font-size: 12px; }
.grid-2 { grid-template-columns: 1fr 1fr; }
.grid-3 { grid-template-columns: 1fr; }
.metrics .metric-label {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #86909c;
}
.metrics strong { color: #1d2129; }
.metrics strong.danger { color: #f53f3f; }
.metrics strong.warn { color: #f7ba2a; }
.metrics strong.ok { color: #00b42a; }
.metric-cell {
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: background 0.15s;
}
.metric-cell:hover { background: #e8f3ff; }
.metric-cell:hover strong:not(.danger):not(.warn):not(.ok) { color: #1677ff; }
.list-metric {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  white-space: nowrap;
}
.metric-link {
  color: #1677ff;
  text-decoration: none;
}
.metric-link:hover { text-decoration: underline; }
.metric-link.danger { color: #f53f3f; font-weight: 600; }
.metric-link.warn { color: #f7ba2a; font-weight: 600; }
.metric-link.ok { color: #00b42a; font-weight: 600; }
.time-offline { color: #f53f3f; }
@media (max-width: 1200px) {
  .card-grid { grid-template-columns: 1fr 1fr; }
  .usage-row { grid-template-columns: 1fr; }
}
</style>
